import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { apiClient, LoginRequest, Verify2FARequest } from "../api/client";
import { AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ModeToggle } from "@/components/mode-toggle";
import { DynamicLogo } from "@/components/DynamicLogo";
import { AUTH_ERRORS, getAuthErrorMessage, isEmailVerificationError } from "@/constants/authErrors";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get email and success message from previous step
  const { email: passedEmail, message } = location.state || {};

  const [loginData, setLoginData] = useState<LoginRequest>({
    email: passedEmail || "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(message || "");
  const [showEmailVerificationLink, setShowEmailVerificationLink] =
    useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setShowEmailVerificationLink(false);

    try {
      const response = await apiClient.login(loginData);

      if (response.requires_2fa) {
        // 2FA is required, show 2FA form
        setRequires2FA(true);
        // Set the partial session token for the 2FA request
        apiClient.setSessionToken(response.session_token);
      } else {
        // Login successful without 2FA
        navigate("/home");
      }
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(
        error.response?.data || error.message || error
      );

      // Check for email verification error
      if (isEmailVerificationError(errorMessage)) {
        setError(AUTH_ERRORS.EMAIL_NOT_VERIFIED);
        setShowEmailVerificationLink(true);
      } else {
        setError(errorMessage);
        setShowEmailVerificationLink(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!loginData.email) {
      setError(AUTH_ERRORS.EMAIL_REQUIRED);
      return;
    }

    setIsResendingVerification(true);
    setError("");
    setSuccessMessage("");

    try {
      await apiClient.resendVerificationEmail({ email: loginData.email });
      setSuccessMessage(
        "Verification email sent! Please check your inbox and then use the verify email page.",
      );
      setShowEmailVerificationLink(false);
      // Navigate to verify email page with the email pre-filled
      navigate("/verify-email", {
        state: {
          email: loginData.email,
          message: "A new verification code has been sent to your email.",
        },
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data ||
        error.message ||
        "Failed to resend verification email.";
      setError(errorMessage);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!totpCode.trim()) {
      setError(AUTH_ERRORS.TFA_CODE_REQUIRED);
      setIsLoading(false);
      return;
    }

    try {
      const verify2FAData: Verify2FARequest = {
        code: totpCode.trim(),
        is_backup_code: isBackupCode,
        remember_device: rememberDevice,
        device_name: rememberDevice
          ? `${navigator.platform} - ${new Date().toLocaleDateString()}`
          : undefined,
      };

      await apiClient.verify2FA(verify2FAData);
      navigate("/home");
    } catch (error: any) {
      const errorMessage = getAuthErrorMessage(
        error.response?.data || error.message || error
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOktaLogin = async () => {
    // Check if user has entered an email
    if (!loginData.email) {
      setError(AUTH_ERRORS.EMAIL_REQUIRED);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Step 1: Discover SSO configuration for this email domain
      const response = await fetch("/api/auth/discover", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: loginData.email.trim() }),
      });

      if (!response.ok) {
        throw new Error("Failed to check SSO configuration");
      }

      const discoveryResult = await response.json();

      if (!discoveryResult.sso_available) {
        setError(AUTH_ERRORS.OKTA_SSO_NOT_CONFIGURED);
        setIsLoading(false);
        return;
      }

      if (discoveryResult.error_message) {
        setError(discoveryResult.error_message);
        setIsLoading(false);
        return;
      }

      // Step 2: Redirect to tenant-specific SSO flow
      window.location.href = discoveryResult.auth_url;
    } catch (err) {
      console.error("[SSO Discovery] Error:", err);
      setError(AUTH_ERRORS.OKTA_SSO_INIT_FAILED);
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Call Google OAuth login endpoint
      // No email required - work email validation happens after OAuth callback
      const response = await fetch("/api/auth/google/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to initiate Google login");
      }

      const result = await response.json();

      if (!result.google_oauth_available) {
        setError(AUTH_ERRORS.GOOGLE_OAUTH_NOT_AVAILABLE);
        setIsLoading(false);
        return;
      }

      // Redirect to Google OAuth
      window.location.href = result.auth_url;
    } catch (err: any) {
      console.error("[Google OAuth] Error:", err);
      setError(getAuthErrorMessage(err));
      setIsLoading(false);
    }
  };

  const handleGitHubLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/github/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to initiate GitHub login");
      }

      const result = await response.json();

      if (!result.github_oauth_available) {
        setError(AUTH_ERRORS.GITHUB_OAUTH_NOT_AVAILABLE);
        setIsLoading(false);
        return;
      }

      // Redirect to GitHub OAuth
      window.location.href = result.auth_url;
    } catch (err: any) {
      console.error("[GitHub OAuth] Error:", err);
      setError(getAuthErrorMessage(err));
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Logo and theme toggle - flow on mobile, absolute on sm+ */}
      <div className="flex items-center justify-between px-6 pb-6 sm:pb-0 sm:block">
        <div className="sm:absolute sm:top-4 sm:left-6">
          <DynamicLogo size="lg" className="drop-shadow-lg" />
        </div>
        <div className="sm:absolute sm:top-6 sm:right-6">
          <ModeToggle />
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-semibold text-foreground">
          Sign in to Kestrel AI
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
            <CardDescription>
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <Alert className="mb-6">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {error}
                  {showEmailVerificationLink && (
                    <div className="mt-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={isResendingVerification}
                        className="text-xs"
                      >
                        {isResendingVerification ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                            Sending verification email...
                          </>
                        ) : (
                          "Resend verification email"
                        )}
                      </Button>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {!requires2FA && (
              <div className="mb-6">
                <div className="space-y-3">
                  {/* Google and GitHub Login Buttons - side by side */}
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Google
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={handleGitHubLogin}
                      disabled={isLoading}
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"
                          fill="currentColor"
                        />
                      </svg>
                      GitHub
                    </Button>
                  </div>

                  {/* Okta Login Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleOktaLogin}
                    disabled={isLoading}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
                        fill="currentColor"
                      />
                      <path
                        d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"
                        fill="currentColor"
                      />
                    </svg>
                    Continue with Okta
                  </Button>
                </div>

                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-background text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!requires2FA ? (
              <form className="space-y-6" onSubmit={handleLogin}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={loginData.email}
                    onChange={(e) =>
                      setLoginData({ ...loginData, email: e.target.value })
                    }
                    placeholder="john@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/request-password-reset"
                      className="text-sm text-primary hover:text-primary/80"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({ ...loginData, password: e.target.value })
                    }
                    placeholder="••••••••"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Signing in...
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-foreground">
                    Two-Factor Authentication
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Please enter the verification code from your authenticator
                    app
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handle2FA}>
                  <div className="space-y-2">
                    <Label htmlFor="totpCode">
                      {isBackupCode ? "Backup Code" : "Verification Code"}
                    </Label>
                    <Input
                      id="totpCode"
                      name="totpCode"
                      type="text"
                      autoComplete="one-time-code"
                      required
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      placeholder={
                        isBackupCode ? "Enter backup code" : "000000"
                      }
                      maxLength={isBackupCode ? 12 : 6}
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isBackupCode}
                        onChange={(e) => setIsBackupCode(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm text-muted-foreground">
                        Use backup code
                      </span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm text-muted-foreground">
                        Remember this device
                      </span>
                    </label>
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      "Verify"
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setRequires2FA(false);
                      setTotpCode("");
                      setIsBackupCode(false);
                      setRememberDevice(false);
                      apiClient.setSessionToken(null);
                    }}
                  >
                    Back to Login
                  </Button>
                </form>
              </div>
            )}

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">
                    Need an account?
                  </span>
                </div>
              </div>

              <div className="mt-4 text-center">
                <Link
                  to="/register"
                  className="text-sm text-primary hover:text-primary/80"
                >
                  Create a new account
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
